/**
 * useUserPreferences — lightweight hook for reading user display preferences.
 * Reads from useUserSettingsStore (singleton); fetches once if not yet loaded.
 * Use in any component that needs dateFormat / timezoneOffset / timezone.
 */

import { useEffect } from 'react';
import { mediator } from '@/application/core';
import { GetUserPreferencesQuery, type UserPreferences } from '@/application/queries/userPreferences';
import { useUserSettingsStore } from './UserSettingsPage.store';

export function useUserPreferences() {
  const data     = useUserSettingsStore((s) => s.data);
  const setData  = useUserSettingsStore((s) => s.setData);
  const setLoading = useUserSettingsStore((s) => s.setLoading);
  const setError  = useUserSettingsStore((s) => s.setError);

  useEffect(() => {
    // Skip if already loaded by any component (store is a singleton)
    if (useUserSettingsStore.getState().data !== null) return;
    let cancelled = false;
    setLoading(true);
    mediator
      .send<UserPreferences>(new GetUserPreferencesQuery())
      .then((prefs) => { if (!cancelled) setData(prefs); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err : new Error(String(err))); });
    return () => { cancelled = true; };
  }, [setData, setError, setLoading]);

  return {
    dateFormat:     data?.dateFormat      ?? '',
    timezoneOffset: data?.timezoneOffset  ?? -999,
    timezone:       data?.timezone        ?? 'Browser/Default',
    prefsLoaded:    data !== null,
  };
}
